import React, { useState, useRef } from 'react';

import useOutsideClick from '@stencil/use-outside-click';
import getTravelType from '@stencil/flights-utils/src/get-travel-type';
import getTravelDates from '@stencil/flights-utils/src/get-travel-dates';
import CloseIcon from '../../../icons/Close';
import {
  FLIGHT_CLASS_OBJECT,
  ONEWAY_AND_ROUNTTRIP_TEXT,
  SOURCE_AND_DESTINATION_TEXT_FOR_DWEB,
  DEPARTURE_AND_ARRIVAL_TEXT_FOR_DWEB,
  PASSENGER_AND_CLASS_TEXT_FOR_DWEB,
  UPDATE_SEARCH_TEXT,
  EMPTY_STRING
} from '@stencil/flights-constants/src/text';

// STYLES
import * as S from './search-widget-ui.styles';

// COMPONENTS
import SearchWidgetAutosuggest from '../SearchWidgetAutoSuggest/search-widget-autosuggest';
import CalendarBlock from '../../Common/CalendarBlock/calendar-block';
import PaxWidget from '../../Common/PaxWidget/pax-widget';
import FareOptions from '../../Common/FareOptions/FareOptionsUI';
import OfferOverlay from '../offer-overlay/offer-overlay';

import { FlexItem } from '../../Common/dweb-common.styles';

import RoundArrowIcon from '@stencil/flights-icons/src/round-arrow-icon';
import ArrowRightLnIcon from '@stencil/flights-icons/src/arrow-right-ln-icon';
import sendDataToNewrelic from '@stencil/send-data-to-new-relic';
import TravelDateWiget from '../../Common/TravelDateWidget/travel-date-widget';

const getPaxText = (pax, flightClass) => {
  let count = 0;
  Object.entries(pax).forEach(([key, value]: [string, number]) => {
    count = count + value;
  });
  return `${count} Traveller(s)${flightClass && flightClass.name ? `, ${flightClass.name}` : ''}`;
};

const SrcField = props => {
  const { label, location, updateLocation } = props;
  return (
    <S.SearchWrapperArea>
      <SearchWidgetAutosuggest label={label} location={location} updateLocation={updateLocation} />
    </S.SearchWrapperArea>
  );
};

const DestField = props => {
  const { label, location, updateLocation, isCityCountry } = props;
  return (
    <S.SearchWrapperArea>
      <SearchWidgetAutosuggest
        label={label}
        location={location}
        updateLocation={updateLocation}
        isCityCountry={isCityCountry}
      />
    </S.SearchWrapperArea>
  );
};

const SearchWidgetUI = ({
  source,
  destination,
  travelType,
  journeyType,
  departure,
  arrival,
  passengers,
  flightClass,
  fareOptionsInfo = null,
  handleFareOptionSelection,
  selectedFareIdentifier,
  updateLocation,
  handleDateSelection,
  handleArrivalCalendarSelection,
  handlePassengersUpdate,
  handleFlightClassUpdate,
  handleJourneyTypeUpdate,
  handleSwapSourceDestination,
  updateSearch,
  checkError,
  gaHandler,
  offerOverlayData = null,
  getFareData,
  fareCalendarData,

  //City country
  travelDate,
  tripDuration,
  isAnyTimeSelected,
  handleAnyTime,
  handleTripDuration,
  handleTravelDate,

  language = 'english',
  displayFlightClass = [],
  isSEO = false,
  isSEM = false,
  displayOnlyAdultPax = false,
  isCityCountry = false
}) => {
  const calendarBlockRef = React.createRef();
  const paxWidgetBlockRef = React.createRef();
  const travelDateRef = useRef();

  const [initialSrcValue, setInitialSrcValue] = useState(source);
  const [initialDestValue, setInitialDestValue] = useState(destination);

  const hasSwapped =
    initialSrcValue.iata === destination.iata && initialDestValue.iata === source.iata;

  const [calendarMode, setCalendarMode] = useState(isSEM || isSEO ? 'departure' : '');
  const [isPaxWidgetVisible, togglePaxWidgetVisible] = useState(false);
  const [isTravelDateWidgetVisible, toggleTravelDateWidget] = useState(false);
  const [error, setError] = useState(EMPTY_STRING);

  const travelDates = getTravelDates();
  const [isSelectedMonth, setSelectedMonth] = useState(
    isAnyTimeSelected ? '' : travelDate && travelDate?.dateToDisplay.split(' ').slice(1).join(' ')
  );

  const closeCalendar = () => {
    setCalendarMode('');
  };

  const togglePaxWidget = val => {
    togglePaxWidgetVisible(val);
    if (val && isPaxWidgetVisible) closeCalendar();
  };

  useOutsideClick(calendarBlockRef, () => {
    setCalendarMode('');
  });

  useOutsideClick(paxWidgetBlockRef, () => {
    togglePaxWidget(false);
  });

  useOutsideClick(travelDateRef, () => {
    toggleTravelDateWidget(!isTravelDateWidgetVisible);
  });

  const isCalendarVisible = calendarMode !== '';

  const updateSrc = src => {
    const newTravelType = getTravelType({ source: src, destination, travelType });
    const locObject = {
      source: src,
      ...(newTravelType === '' ? null : { travelType: newTravelType })
    };
    setInitialSrcValue(src);
    setInitialDestValue(destination);
    updateLocation(locObject);
  };

  const updateDest = dest => {
    const newTravelType = getTravelType({ destination: dest, source, travelType });
    const locObject = {
      destination: dest,
      ...(newTravelType === '' ? null : { travelType: newTravelType })
    };
    setInitialSrcValue(source);
    setInitialDestValue(dest);
    updateLocation(locObject);
  };

  const updateJourneyType = jt => {
    if (calendarMode !== '') {
      setCalendarMode('');
    }
    if (jt !== journeyType) {
      handleJourneyTypeUpdate(jt);
      if (jt === 'rt') {
        setCalendarMode('arrival');
      }
    }
  };

  const getFlightclass = (displayFlightClass, flightClassObject) => {
    const flightClassToBeDisplayed = [];
    for (let i = 0; i < displayFlightClass.length; i++) {
      const flightClass = flightClassObject.find(
        flightClass => flightClass.name.toLowerCase() == displayFlightClass[i]
      );
      if (flightClass) {
        flightClassToBeDisplayed.push(flightClass);
      }
    }
    return flightClassToBeDisplayed;
  };

  const searchFlight = () => {
    const error = checkError() || '';
    setError(error);
    if (error === '') {
      try {
        updateSearch();
      } catch (err) {
        sendDataToNewrelic('Search Widget onSearch', `Failed with error: ${err}`);
      }
    } else {
      setTimeout(() => {
        setError(EMPTY_STRING);
      }, 10000);
    }
  };

  const handleTravelDateMonth = month => {
    setSelectedMonth(month);
    toggleTravelDateWidget(false);
    handleTravelDate(month);
  };

  const handleTravelDateAnyTime = () => {
    toggleTravelDateWidget(false);
    handleAnyTime();
  };

  const handleTravelDateVisible = () => {
    toggleTravelDateWidget(!isTravelDateWidgetVisible);
    togglePaxWidgetVisible(false);
  };

  const { ONEWAY_TEXT, ROUNTTRIP_TEXT } = ONEWAY_AND_ROUNTTRIP_TEXT[language];
  const { SOURCE_TEXT, DESTINATION_TEXT } = SOURCE_AND_DESTINATION_TEXT_FOR_DWEB[language];
  const { DEPART_TEXT, ARRIVAL_TEXT } = DEPARTURE_AND_ARRIVAL_TEXT_FOR_DWEB[language];

  return (
    <>
      <S.OuterWrapDiv>
        <S.RadioWrapper>
          <S.ClassWrap onClick={() => updateJourneyType('ow')}>
            <S.RadioButton
              type="radio"
              name="flt-class-group"
              value={'hello'}
              onChange={() => {}}
              checked={journeyType === 'ow'}
            />
            <S.RadioLabel htmlFor="radio-1">
              <S.LabelText>{ONEWAY_TEXT}</S.LabelText>
            </S.RadioLabel>
          </S.ClassWrap>
          <S.ClassWrap onClick={() => updateJourneyType('rt')}>
            <S.RadioButton
              type="radio"
              name="flt-class-group"
              value={'hello'}
              onChange={() => {}}
              checked={journeyType === 'rt'}
            />
            <S.RadioLabel htmlFor="radio-1">
              <S.LabelText>{ROUNTTRIP_TEXT}</S.LabelText>
            </S.RadioLabel>
          </S.ClassWrap>
        </S.RadioWrapper>
        <S.WrapperStyle>
          <FlexItem>
            {isCityCountry ? (
              <>
                <SrcField label={SOURCE_TEXT} location={source} updateLocation={updateSrc} />
                <S.SwapIconWrapper>
                  <S.SearchLabelStyle>&nbsp;</S.SearchLabelStyle>
                  <ArrowRightLnIcon fill="#ffffff" />
                </S.SwapIconWrapper>
                <DestField
                  label={DESTINATION_TEXT}
                  location={destination}
                  updateLocation={updateDest}
                  isCityCountry={isCityCountry}
                />
                <TravelDateWiget
                  travelDateRef={travelDateRef}
                  isAnyTimeSelected={isAnyTimeSelected}
                  isSelectedMonth={isSelectedMonth}
                  isTravelDateWidgetVisible={isTravelDateWidgetVisible}
                  travelDates={travelDates}
                  travelDate={travelDate}
                  journeyType={journeyType}
                  tripDuration={tripDuration}
                  handleTravelDateVisible={handleTravelDateVisible}
                  handleTravelDateAnyTime={handleTravelDateAnyTime}
                  handleTravelDateMonth={handleTravelDateMonth}
                  handleTripDuration={handleTripDuration}
                />
              </>
            ) : (
              <>
                {!hasSwapped ? (
                  <SrcField label={SOURCE_TEXT} location={source} updateLocation={updateSrc} />
                ) : (
                  <DestField label={SOURCE_TEXT} location={source} updateLocation={updateSrc} />
                )}
                <S.SwapIconWrapper>
                  <S.SearchLabelStyle>&nbsp;</S.SearchLabelStyle>
                  <RoundArrowIcon
                    fill="#ffffff"
                    onClick={handleSwapSourceDestination}
                    loading="lazy"
                  />
                </S.SwapIconWrapper>
                {!hasSwapped ? (
                  <DestField
                    label={DESTINATION_TEXT}
                    location={destination}
                    updateLocation={updateDest}
                  />
                ) : (
                  <SrcField
                    label={DESTINATION_TEXT}
                    location={destination}
                    updateLocation={updateDest}
                  />
                )}
                <S.DepartureArrivalWrapper id="search-widget-calendar-element">
                  <div
                    onClick={() => {
                      setCalendarMode('departure');
                      if (isPaxWidgetVisible) togglePaxWidget(false);
                    }}
                  >
                    <S.SearchLabelStyle>{DEPART_TEXT}</S.SearchLabelStyle>
                    <S.SearchInputStyle
                      type="text"
                      value={(departure && departure.dateToDisplay) || ''}
                      placeholder="Travel Dates"
                      id="search-widget-checkin-input"
                      editable="false"
                      hasValue={departure}
                    />
                  </div>
                  {(calendarMode === 'departure' && (
                    <CalendarBlock
                      ref={calendarBlockRef}
                      isSearchWidget={true}
                      isDeparture={calendarMode === 'departure'}
                      isArrival={false}
                      onSelection={() => setCalendarMode('')}
                      handleOnDateClick={handleDateSelection}
                      departure={departure}
                      arrival={null}
                      getFareData={getFareData}
                      fareCalendarData={fareCalendarData}
                    />
                  )) ||
                    null}
                </S.DepartureArrivalWrapper>
                <S.DepartureArrivalWrapper>
                  <div
                    onClick={() => {
                      handleArrivalCalendarSelection();
                      setCalendarMode('arrival');
                      if (isPaxWidgetVisible) togglePaxWidget(false);
                    }}
                  >
                    <S.SearchLabelStyle>{ARRIVAL_TEXT}</S.SearchLabelStyle>
                    <S.SearchInputStyle
                      type="text"
                      value={(departure && arrival && arrival.dateToDisplay) || ''}
                      placeholder="Travel Dates"
                      editable="false"
                      hasValue={arrival}
                    />
                  </div>
                  {departure && arrival && arrival.dateToDisplay ? (
                    <CloseIcon
                      width="25px"
                      height="25px"
                      fill="#fff"
                      style={{
                        position: 'absolute',
                        top: '70px',
                        marginLeft: '100px',
                        cursor: 'pointer'
                      }}
                      onClick={() => updateJourneyType('ow')}
                      loading="lazy"
                    />
                  ) : null}
                  {(calendarMode === 'arrival' && (
                    <CalendarBlock
                      ref={calendarBlockRef}
                      isSearchWidget={true}
                      isDeparture={false}
                      isArrival={true}
                      onSelection={() => closeCalendar()}
                      handleOnDateClick={handleDateSelection}
                      departure={departure}
                      arrival={arrival}
                      getFareData={getFareData}
                      fareCalendarData={fareCalendarData}
                    />
                  )) ||
                    null}
                </S.DepartureArrivalWrapper>
              </>
            )}
            <S.PaxWrapperStyle
              onClick={() => {
                if (isCalendarVisible) closeCalendar();
                if (!isPaxWidgetVisible) {
                  togglePaxWidget(true);
                }
              }}
            >
              <S.SearchLabelStyle>{PASSENGER_AND_CLASS_TEXT_FOR_DWEB[language]}</S.SearchLabelStyle>
              <S.SearchInputStyle
                type="text"
                value={getPaxText(passengers, flightClass)}
                editable="false"
                hasValue={true}
              />
              {(isPaxWidgetVisible && (
                <PaxWidget
                  ref={paxWidgetBlockRef}
                  togglePaxWidget={togglePaxWidget}
                  gaHandler={gaHandler}
                  passengers={passengers}
                  flightClass={flightClass}
                  flightClassList={
                    displayFlightClass && displayFlightClass.length > 0
                      ? getFlightclass(displayFlightClass, FLIGHT_CLASS_OBJECT)
                      : FLIGHT_CLASS_OBJECT
                  }
                  paxDoneHandler={handlePassengersUpdate}
                  classDoneHandler={handleFlightClassUpdate}
                  language={language}
                  kidsNotAllowed={displayOnlyAdultPax}
                />
              )) ||
                ''}
            </S.PaxWrapperStyle>
          </FlexItem>
          <S.UpdateSearchBtn onClick={searchFlight}>
            {UPDATE_SEARCH_TEXT[language]}
          </S.UpdateSearchBtn>
        </S.WrapperStyle>
        {fareOptionsInfo && !isCityCountry && Object.keys(fareOptionsInfo).length > 0 ? (
          <S.FareOptionsWrapper>
            <FareOptions
              fareOptionsInfo={fareOptionsInfo}
              travelType={travelType}
              journeyType={journeyType}
              passengers={passengers}
              flightClass={flightClass}
              arrival={arrival}
              departure={departure}
              handleFareOptionSelection={handleFareOptionSelection}
              selectedFareIdentifier={selectedFareIdentifier}
            />
          </S.FareOptionsWrapper>
        ) : null}
        {error !== EMPTY_STRING ? (
          <S.WrapperStyle>
            <S.ErrorWrap>
              <div>
                <S.ErrorStatus>ERROR:</S.ErrorStatus>
                <S.ErrorText>{error}</S.ErrorText>
              </div>
            </S.ErrorWrap>
          </S.WrapperStyle>
        ) : null}
      </S.OuterWrapDiv>
      {offerOverlayData ? <OfferOverlay offerOverlayData={offerOverlayData} /> : null}
    </>
  );
};

export default SearchWidgetUI;
